'use client';

import { c as _c } from "react/compiler-runtime";
import { type RefObject, useEffect, useState } from 'react';
import { clampFooterOffsetToParent, detectMobileSafari, getKeyboardOffset } from './utils';
const DEBOUNCE_MS = 100;
function createDebounced(fn: () => void, delay: number) {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const debounced = () => {
    if (timer !== undefined) {
      clearTimeout(timer);
    }
    timer = setTimeout(() => {
      timer = undefined;
      fn();
    }, delay);
  };
  const cancel = () => {
    if (timer !== undefined) {
      clearTimeout(timer);
      timer = undefined;
    }
  };
  return {
    debounced,
    cancel
  };
}
export function useVisualViewportKeyboardOffset(footerRef) {
  const $ = _c(6);
  const [offset, setOffset] = useState(0);
  const [enabled, setEnabled] = useState(false);
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => {
      setEnabled(detectMobileSafari());
    };
    t1 = [];
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  useEffect(t0, t1);
  let t2;
  let t3;
  if ($[2] !== enabled || $[3] !== footerRef) {
    t2 = () => {
      if (!enabled) {
        setOffset(0);
        return;
      }
      const viewport = window.visualViewport;
      if (!viewport) {
        return;
      }
      let lastOffset = 0;
      const commit = () => {
        const footer = footerRef.current;
        const defaultOffset = getKeyboardOffset();
        const footerRelativeOffset = footer ? clampFooterOffsetToParent(footer, defaultOffset) : defaultOffset;
        if (footerRelativeOffset !== lastOffset) {
          lastOffset = footerRelativeOffset;
          setOffset(footerRelativeOffset);
        }
      };
      const {
        debounced: debouncedCommit,
        cancel
      } = createDebounced(commit, DEBOUNCE_MS);
      commit();
      viewport.addEventListener("resize", commit);
      viewport.addEventListener("scroll", debouncedCommit);
      window.addEventListener("resize", debouncedCommit);
      return () => {
        cancel();
        viewport.removeEventListener("resize", commit);
        viewport.removeEventListener("scroll", debouncedCommit);
        window.removeEventListener("resize", debouncedCommit);
      };
    };
    t3 = [enabled, footerRef];
    $[2] = enabled;
    $[3] = footerRef;
    $[4] = t2;
    $[5] = t3;
  } else {
    t2 = $[4];
    t3 = $[5];
  }
  useEffect(t2, t3);
  return enabled ? offset : 0;
}